import {useLayoutEffect, useRef, useState, type CSSProperties} from 'react';
import {ChevronLeft, ChevronRight} from 'lucide-react';
import {parseMultiProductDescriptions, displayBlocks, messageIdentifiesSingletonProduct, messageMentionsProductNamePrefix, numberedSingletonOrdinal, prefixedSingletonCategory, validBlocks, visualGroupTitle, type AssistantBlock, type TweChatMessage, type TweResultGroup} from '../constructor/assistantModel';
import type {EventBus} from '../host/eventBus';
import type {EventContext} from '../host/types';
import {resolveAgentContent} from '../content';
import {AssistantMarkdown} from './AssistantMarkdown';
import {ProductGroup} from './ProductGroup';
import {FollowUpRefinement} from './FollowUpRefinement';
import {scrollProductRail} from './productRailScroll';
import {SuggestedQuestions} from './SuggestedQuestions';

type MessageBlock = Extract<AssistantBlock, {type: 'message'}>;
type ResultBlock = Extract<AssistantBlock, {type: 'search_result'}>;
type SingletonRecommendationItem = {type: 'singleton_recommendation'; messageBlock: MessageBlock; resultBlock: ResultBlock; group: TweResultGroup};
type RecommendationRailEntry = {resultBlock: ResultBlock; group: TweResultGroup; messageBlock?: MessageBlock; ordinal?: number; productIndex?: number; descriptionText?: string; sourceMessageId?: string};
type NumberedRunEntry = RecommendationRailEntry & {ordinal: number};
type PresentationItem =
  | {type: 'block'; block: AssistantBlock}
  | SingletonRecommendationItem
  | {type: 'numbered_recommendation_group'; entries: NumberedRunEntry[]}
  | {type: 'recommendation_group'; entries: RecommendationRailEntry[]; resultOnly?: boolean};

type NumberedCandidate = NumberedRunEntry & {consumed: number};
export type RecommendationRowModel = {key: string; title?: string; entries: RecommendationRailEntry[]; hideEntryHeading: boolean};

const normalizedRowTitle = (value: string) => value.normalize('NFKC').replace(/\s+/g,' ').trim().toLocaleLowerCase();
type CategoryRunKey = {kind: 'repeated-prefix'; title: string} | {kind: 'numbered-category'; title: string; number: number};
function categoryRunKey(group: TweResultGroup): CategoryRunKey | undefined {
  if (numberedSingletonOrdinal(group) !== undefined) return undefined;
  const prefix = prefixedSingletonCategory(group)?.trim();
  if (!prefix || /^\d+$/.test(prefix)) return undefined;
  const numbered = prefix.match(/^(.+\S)\s+(\d+)$/);
  if (numbered && !/^\d+$/.test(numbered[1])) {
    const number = Number(numbered[2]);
    if (Number.isSafeInteger(number) && number > 0) return {kind:'numbered-category',title:numbered[1].trim(),number};
  }
  return {kind:'repeated-prefix',title:prefix};
}
function continuesCategory(first: CategoryRunKey, previous: CategoryRunKey, next: CategoryRunKey): boolean {
  return first.kind === next.kind && normalizedRowTitle(first.title) === normalizedRowTitle(next.title)
    && (first.kind === 'repeated-prefix' || (previous.kind === 'numbered-category' && next.kind === 'numbered-category' && next.number === previous.number + 1));
}
function numberedCategoryTitle(entry: RecommendationRailEntry): {title: string; number: number} | undefined {
  const heading=visualGroupTitle(entry.group).trim();
  const match=heading.match(/^(.+\S)\s+(\d+)$/);
  if(!match||/^\d+$/.test(match[1])) return undefined;
  const number=Number(match[2]);
  return Number.isSafeInteger(number)&&number>0?{title:match[1].trim(),number}:undefined;
}

/** Split only when adjacent headings provide repeated or sequential category evidence. */
export function recommendationRows(entries: RecommendationRailEntry[], numbered: boolean): RecommendationRowModel[] {
  const plain=():RecommendationRowModel[]=>[{key:'all',entries,hideEntryHeading:false}];
  if(numbered||entries.length<2) return plain();
  const rows:RecommendationRowModel[]=[]; let fallback:RecommendationRailEntry[]=[]; let detected=false;
  const flushFallback=()=>{if(fallback.length){rows.push({key:`fallback-${rows.length}`,entries:fallback,hideEntryHeading:false});fallback=[];}};
  for(let index=0;index<entries.length;){
    const heading=visualGroupTitle(entries[index].group).trim();
    if(heading){
      const key=normalizedRowTitle(heading); let end=index+1;
      while(end<entries.length&&normalizedRowTitle(visualGroupTitle(entries[end].group))===key) end+=1;
      if(end-index>=2){flushFallback();rows.push({key:`repeated-${index}`,title:heading,entries:entries.slice(index,end),hideEntryHeading:true});detected=true;index=end;continue;}
    }
    const category=numberedCategoryTitle(entries[index]);
    if(category){
      const key=normalizedRowTitle(category.title); let end=index+1; let previous=category.number;
      while(end<entries.length){const next=numberedCategoryTitle(entries[end]);if(!next||normalizedRowTitle(next.title)!==key||next.number!==previous+1)break;previous=next.number;end+=1;}
      if(end-index>=2){flushFallback();rows.push({key:`category-${index}`,title:category.title,entries:entries.slice(index,end),hideEntryHeading:false});detected=true;index=end;continue;}
    }
    fallback.push(entries[index]);index+=1;
  }
  flushFallback();
  return detected?rows:plain();
}

function singletonPairAt(blocks: AssistantBlock[], index: number, message: TweChatMessage): SingletonRecommendationItem | undefined {
  const block = blocks[index];
  const next = blocks[index + 1];
  if (block?.type !== 'message' || next?.type !== 'search_result') return undefined;
  const group = message.groups?.[next.groupIndex];
  if (!group || group.searchResults.length !== 1 || !messageIdentifiesSingletonProduct(block.text, group)) return undefined;
  return {type: 'singleton_recommendation', messageBlock: block, resultBlock: next, group};
}

/** The title itself supplies grouping evidence only for an adjacent run.
 * Never use this candidate alone to pair an isolated message and product. */
function prefixedSingletonPairAt(blocks: AssistantBlock[], index: number, message: TweChatMessage): SingletonRecommendationItem | undefined {
  const block = blocks[index];
  const next = blocks[index + 1];
  if (block?.type !== 'message' || !block.text.trim() || next?.type !== 'search_result') return undefined;
  const group = message.groups?.[next.groupIndex];
  if (!group || !categoryRunKey(group)) return undefined;
  return {type: 'singleton_recommendation', messageBlock: block, resultBlock: next, group};
}

function prefixedRunAt(blocks: AssistantBlock[], index: number, message: TweChatMessage): {entries: RecommendationRailEntry[]; nextIndex: number} | undefined {
  const first = prefixedSingletonPairAt(blocks, index, message);
  if (!first) return undefined;
  const category = categoryRunKey(first.group)!;
  let previous = category;
  const entries: RecommendationRailEntry[] = [];
  let needsFallback = false;
  let nextIndex = index;
  while (nextIndex < blocks.length) {
    const pair = prefixedSingletonPairAt(blocks, nextIndex, message);
    const block = blocks[nextIndex];
    const bareGroup = !pair && block?.type === 'search_result' ? message.groups?.[block.groupIndex] : undefined;
    // Constructor may emit one explanatory pair followed by bare results from
    // the same category. They have no message to invent or attach to a card.
    const bare = bareGroup && categoryRunKey(bareGroup)
      ? {resultBlock: block as ResultBlock, group: bareGroup} : undefined;
    const candidate = pair || bare;
    const nextCategory = candidate && categoryRunKey(candidate.group);
    if (!candidate || !nextCategory || (entries.length > 0 && !continuesCategory(category, previous, nextCategory))) break;
    const strict = pair && singletonPairAt(blocks, nextIndex, message);
    entries.push(strict || candidate);
    if (!strict) needsFallback = true;
    previous = nextCategory;
    nextIndex += pair ? 2 : 1;
  }
  // Let the established strict path and recommendationRows() handle all-strict
  // runs. A lone title-backed candidate must never become a paired card.
  return entries.length >= 2 && needsFallback ? {entries, nextIndex} : undefined;
}

/** Consume an entire M...M R...R batch only if every description uniquely
 * identifies the product at the same offset and all titles form one row. */
function batchedRunAt(blocks: AssistantBlock[], index: number, message: TweChatMessage): {entries: RecommendationRailEntry[]; nextIndex: number} | undefined {
  if (blocks[index]?.type !== 'message') return undefined;
  let resultStart = index;
  while (blocks[resultStart]?.type === 'message') resultStart++;
  const count = resultStart - index;
  if (count < 2) return undefined;
  let resultEnd = resultStart;
  while (blocks[resultEnd]?.type === 'search_result') resultEnd++;
  if (resultEnd - resultStart !== count) return undefined;
  const entries: RecommendationRailEntry[] = [];
  let firstKey: CategoryRunKey | undefined;
  let previousKey: CategoryRunKey | undefined;
  for (let offset = 0; offset < count; offset++) {
    const description = blocks[index + offset] as MessageBlock;
    const resultBlock = blocks[resultStart + offset] as ResultBlock;
    const group = message.groups?.[resultBlock.groupIndex];
    const key = group && categoryRunKey(group);
    if (!group || !key || !description.text.trim() || (firstKey && !continuesCategory(firstKey, previousKey!, key))) return undefined;
    firstKey ||= key;
    previousKey = key;
    const results = blocks.slice(resultStart, resultEnd) as ResultBlock[];
    if (!messageIdentifiesSingletonProduct(description.text, group)
      || results.some(other => other !== resultBlock && message.groups?.[other.groupIndex]
        && messageIdentifiesSingletonProduct(description.text, message.groups[other.groupIndex]))) return undefined;
    entries.push({messageBlock:description,resultBlock,group});
  }
  return {entries,nextIndex:resultEnd};
}

/** An invalid message batch must not let its final description accidentally
 * pair with the first product (or start a title-backed run). */
function unsafeBatchBoundaryAt(blocks: AssistantBlock[], index: number, message: TweChatMessage): boolean {
  if (blocks[index]?.type !== 'message' || blocks[index + 1]?.type !== 'search_result') return false;
  let start=index;
  while (start>0 && blocks[start-1].type==='message') start--;
  if (start===index) return false;
  let resultEnd=index+1;
  while (blocks[resultEnd]?.type==='search_result') resultEnd++;
  if (resultEnd-index-1<2) return false;
  const results=blocks.slice(index+1,resultEnd) as ResultBlock[];
  const prior=blocks.slice(start,index) as MessageBlock[];
  const firstGroup=message.groups?.[results[0].groupIndex];
  return prior.some(description => results.some(result => message.groups?.[result.groupIndex]
    && messageIdentifiesSingletonProduct(description.text,message.groups[result.groupIndex])))
    || !firstGroup || !messageIdentifiesSingletonProduct((blocks[index] as MessageBlock).text,firstGroup);
}

function numberedCandidateAt(blocks: AssistantBlock[], index: number, message: TweChatMessage): NumberedCandidate | undefined {
  const pair = singletonPairAt(blocks, index, message);
  if (pair) {
    const ordinal = numberedSingletonOrdinal(pair.group);
    return ordinal === undefined ? undefined : {...pair, ordinal, consumed: 2};
  }
  const block = blocks[index];
  if (block?.type !== 'search_result') return undefined;
  const group = message.groups?.[block.groupIndex];
  if (!group) return undefined;
  const ordinal = numberedSingletonOrdinal(group);
  return ordinal === undefined ? undefined : {ordinal, resultBlock: block, group, consumed: 1};
}

/** Display-only grouping of adjacent result-only singleton blocks. The
 * numbered-stage path takes precedence and numbered titles end this run. */
function resultOnlyGroupAt(blocks: AssistantBlock[], index: number, message: TweChatMessage): TweResultGroup | undefined {
  const block = blocks[index];
  if (block?.type !== 'search_result') return undefined;
  const group = message.groups?.[block.groupIndex];
  const title = group?.originalTitle?.trim() || group?.group?.display_name?.trim() || '';
  return group?.searchResults.length === 1 && !/^\d+[.)]\s/.test(title) ? group : undefined;
}
function resultOnlyRunAt(blocks: AssistantBlock[], index: number, message: TweChatMessage): {entries: RecommendationRailEntry[]; nextIndex: number} | undefined {
  // An unconsumed M...M R...R batch that names its products is ambiguous:
  // preserve those descriptions/results rather than regrouping a suffix.
  // Generic introductory prose without actual name matches is not a batch.
  let resultStart=index;
  while (blocks[resultStart-1]?.type === 'search_result') resultStart--;
  let messageStart=resultStart;
  while (blocks[messageStart-1]?.type === 'message') messageStart--;
  if (resultStart-messageStart >= 2) {
    let resultEnd=resultStart;
    while (blocks[resultEnd]?.type === 'search_result') resultEnd++;
    const results=blocks.slice(resultStart,resultEnd) as ResultBlock[];
    const descriptions=blocks.slice(messageStart,resultStart) as MessageBlock[];
    if (descriptions.some(description=>results.some(result=>{
      const group=message.groups?.[result.groupIndex];
      return group && messageIdentifiesSingletonProduct(description.text,group);
    }))) return undefined;
  }
  if (resultOnlyGroupAt(blocks,index - 1,message)) return undefined;
  const entries: RecommendationRailEntry[] = [];
  let nextIndex = index;
  while (resultOnlyGroupAt(blocks,nextIndex,message)) {
    const block = blocks[nextIndex] as ResultBlock;
    const group = message.groups![block.groupIndex];
    entries.push({resultBlock: block, group});
    nextIndex++;
  }
  return entries.length >= 2 ? {entries, nextIndex} : undefined;
}

/** A category-wide introduction belongs above its result-only row, not in the
 * first card. A product-identifying message still follows the existing pair path. */
function genericIntroBeforeResultRunAt(blocks: AssistantBlock[], index: number, message: TweChatMessage): boolean {
  const block = blocks[index];
  const next = blocks[index + 1];
  if (block?.type !== 'message' || !block.text.trim() || next?.type !== 'search_result') return false;
  const firstGroup = message.groups?.[next.groupIndex];
  const firstKey = firstGroup && categoryRunKey(firstGroup);
  if (!firstGroup || firstKey?.kind !== 'repeated-prefix' || messageIdentifiesSingletonProduct(block.text, firstGroup)
    || messageMentionsProductNamePrefix(block.text, firstGroup)) return false;
  let previous: CategoryRunKey = firstKey;
  let count = 1;
  for (let nextIndex = index + 2; blocks[nextIndex]?.type === 'search_result'; nextIndex++) {
    const result = blocks[nextIndex] as ResultBlock;
    const group = message.groups?.[result.groupIndex];
    const key = group && categoryRunKey(group);
    if (!key || !continuesCategory(firstKey, previous, key)) break;
    previous = key;
    count++;
  }
  return count >= 2;
}

function multiProductDescriptionPairAt(blocks: AssistantBlock[], index: number, message: TweChatMessage): RecommendationRailEntry[] | undefined {
  const source = blocks[index]; const resultBlock = blocks[index+1];
  if (source?.type !== 'message' || resultBlock?.type !== 'search_result') return undefined;
  const group = message.groups?.[resultBlock.groupIndex];
  const descriptions = group && parseMultiProductDescriptions(source.text,group);
  return descriptions?.map(({productIndex,descriptionText})=>({resultBlock,group:group!,productIndex,descriptionText,sourceMessageId:source.id}));
}

/** Presentation-only grouping. Stored blocks, callbacks and event order remain untouched. */
export function orderedPresentation(message: TweChatMessage): PresentationItem[] {
  const blocks = displayBlocks(message);
  const canGroup = Boolean(message.blocks && validBlocks(message.blocks, message.groups));
  const items: PresentationItem[] = [];
  for (let index = 0; index < blocks.length; index += 1) {
    if (canGroup) {
      const multi = multiProductDescriptionPairAt(blocks,index,message);
      if (multi) {
        items.push({type:'recommendation_group',entries:multi});
        index++;
        continue;
      }
      const batch = batchedRunAt(blocks,index,message);
      if (batch) {
        items.push({type:'recommendation_group',entries:batch.entries});
        index=batch.nextIndex-1;
        continue;
      }
      const first = numberedCandidateAt(blocks, index, message);
      if (first) {
        const entries: NumberedRunEntry[] = [first];
        let nextIndex = index + first.consumed;
        while (nextIndex < blocks.length) {
          const next = numberedCandidateAt(blocks, nextIndex, message);
          if (!next || next.ordinal !== entries.at(-1)!.ordinal + 1) break;
          entries.push(next);
          nextIndex += next.consumed;
        }
        if (entries.length > 1) {
          items.push({type: 'numbered_recommendation_group', entries});
          index = nextIndex - 1;
          continue;
        }
      }
      const resultOnly = resultOnlyRunAt(blocks, index, message);
      if (resultOnly) {
        items.push({type: 'recommendation_group', entries: resultOnly.entries, resultOnly: true});
        index = resultOnly.nextIndex - 1;
        continue;
      }
      if (genericIntroBeforeResultRunAt(blocks,index,message)) {
        items.push({type:'block',block:blocks[index]});
        continue;
      }
      if (unsafeBatchBoundaryAt(blocks,index,message)) {
        items.push({type:'block',block:blocks[index]});
        continue;
      }
      const pair = singletonPairAt(blocks, index, message);
      const prefixedRun = prefixedRunAt(blocks, index, message);
      if (prefixedRun) {
        items.push({type: 'recommendation_group', entries: prefixedRun.entries});
        index = prefixedRun.nextIndex - 1;
        continue;
      }
      if (pair) {
        if (numberedSingletonOrdinal(pair.group) === undefined) {
          const entries = [pair];
          let nextIndex = index + 2;
          while (nextIndex < blocks.length) {
            // A strict pair may be the first item in a later category run
            // containing shortened names. Leave it for that run, not this one.
            if (prefixedRunAt(blocks, nextIndex, message)) break;
            const next = singletonPairAt(blocks, nextIndex, message);
            if (!next || numberedSingletonOrdinal(next.group) !== undefined) break;
            const currentKey=categoryRunKey(entries.at(-1)!.group);
            const firstKey=categoryRunKey(entries[0].group);
            const nextKey=categoryRunKey(next.group);
            if (firstKey?.kind==='numbered-category' && (!currentKey || !nextKey || !continuesCategory(firstKey,currentKey,nextKey))) break;
            entries.push(next);
            nextIndex += 2;
          }
          if (entries.length > 1) {
            items.push({type: 'recommendation_group', entries});
            index = nextIndex - 1;
            continue;
          }
        }
        items.push(pair);
        index += 1;
        continue;
      }
    }
    items.push({type: 'block', block: blocks[index]});
  }
  return items;
}

function RecommendationCopy({messageBlock, group, heading, descriptionText}: {messageBlock?: MessageBlock; group: TweResultGroup; heading: string; descriptionText?: string}) {
  const description = group.description?.trim();
  const step = /^\d+$/.test(heading);
  if (!messageBlock && !description && descriptionText === undefined) return heading ? <p className={`twe-drinks-advisor__recommendation-label${step ? ' twe-drinks-advisor__recommendation-label--step' : ''}`} {...(step && {'aria-label': `Recommendation ${heading}`})}>{heading}</p> : null;
  return <div className="twe-drinks-advisor__recommendation-copy" {...(messageBlock && {'data-paired-message-id': messageBlock.id})}>
    {heading && <p className={`twe-drinks-advisor__recommendation-label${step ? ' twe-drinks-advisor__recommendation-label--step' : ''}`} {...(step && {'aria-label': `Recommendation ${heading}`})}>{heading}</p>}
    {descriptionText !== undefined ? <AssistantMarkdown text={descriptionText}/> : messageBlock && <AssistantMarkdown text={messageBlock.text}/>}
    {description && <AssistantMarkdown text={description} className="twe-drinks-advisor__group-description twe-drinks-advisor__recommendation-description"/>}
  </div>;
}

function SingletonRecommendation({item, message, bus, prefix}: {item: SingletonRecommendationItem; message: TweChatMessage; bus: EventBus; prefix: string}) {
  const heading = visualGroupTitle(item.group);
  return <div className="twe-drinks-advisor__content-block twe-drinks-advisor__recommendation" data-content-type="singleton_recommendation" data-block-id={`${item.messageBlock.id}:${item.resultBlock.id}`}>
    <RecommendationCopy messageBlock={item.messageBlock} group={item.group} heading={heading}/>
    <ProductGroup key={`${prefix}:${item.resultBlock.id}`} group={item.group} message={message} index={item.resultBlock.groupIndex} bus={bus} paired/>
  </div>;
}

function RecommendationRow({row, message, bus, prefix, label, legacy = false}: {row: RecommendationRowModel; message: TweChatMessage; bus: EventBus; prefix: string; label: string; legacy?: boolean}) {
  const rail = useRef<HTMLDivElement>(null);
  const [navigation, setNavigation] = useState({overflow: false, left: false, right: false});
  useLayoutEffect(() => {
    const element = rail.current;
    if (!element) return;
    const measure = () => {
      const overflow = element.scrollWidth > element.clientWidth + 1;
      const left = overflow && element.scrollLeft > 1;
      const right = overflow && element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
      setNavigation(previous => previous.overflow === overflow && previous.left === left && previous.right === right ? previous : {overflow, left, right});
    };
    measure();
    element.addEventListener('scroll', measure, {passive: true});
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    Array.from(element.children).forEach(child => observer?.observe(child));
    return () => { element.removeEventListener('scroll', measure); window.removeEventListener('resize', measure); observer?.disconnect(); };
  }, [row.entries.length]);
  const scroll = (direction: -1 | 1) => scrollProductRail(rail.current, direction);
  const rowStyle = {'--asa-row-count': row.entries.length} as CSSProperties;
  const cards=row.entries.map(entry => {
    const heading = row.hideEntryHeading ? '' : visualGroupTitle(entry.group);
    return <article key={`${prefix}:${entry.resultBlock.id}:${entry.productIndex ?? 'all'}`} className="twe-drinks-advisor__numbered-run-item twe-drinks-advisor__recommendation-rail-item" role="listitem" {...(entry.productIndex !== undefined && {'data-block-id': `${entry.resultBlock.id}:${entry.productIndex}`})} {...(entry.ordinal !== undefined && {'data-ordinal': entry.ordinal})} {...((entry.sourceMessageId || entry.messageBlock?.id) && {'data-paired-message-id': entry.sourceMessageId || entry.messageBlock?.id})}>
      <RecommendationCopy messageBlock={entry.messageBlock} group={entry.group} heading={heading} descriptionText={entry.descriptionText}/>
      <ProductGroup group={entry.group} message={message} index={entry.resultBlock.groupIndex} bus={bus} paired productIndex={entry.productIndex}/>
    </article>;
  });
  if(legacy) return <>
    {navigation.overflow && <div className="twe-drinks-advisor__numbered-run-controls"><span>{label}</span><div className="twe-drinks-advisor__strip-controls"><button type="button" aria-label={`Scroll ${label.toLowerCase()} left`} disabled={!navigation.left} onClick={() => scroll(-1)}><ChevronLeft size={17}/></button><button type="button" aria-label={`Scroll ${label.toLowerCase()} right`} disabled={!navigation.right} onClick={() => scroll(1)}><ChevronRight size={17}/></button></div></div>}
    <div ref={rail} style={rowStyle} className="twe-drinks-advisor__numbered-run-rail" role="list" aria-label={label} tabIndex={navigation.overflow ? 0 : -1} onKeyDown={event => { if (event.key === 'ArrowLeft') {event.preventDefault(); scroll(-1);} else if (event.key === 'ArrowRight') {event.preventDefault(); scroll(1);} }}>{cards}</div>
  </>;
  return <section className="twe-drinks-advisor__recommendation-row" aria-label={label} data-row-title={row.title}>
    {(row.title||navigation.overflow)&&<div className="twe-drinks-advisor__recommendation-row-header">
      {row.title?<h3 className="twe-drinks-advisor__recommendation-row-title">{row.title}</h3>:<span>{label}</span>}
      {navigation.overflow&&<div className="twe-drinks-advisor__strip-controls"><button type="button" aria-label={`Scroll ${label.toLowerCase()} left`} disabled={!navigation.left} onClick={() => scroll(-1)}><ChevronLeft size={17}/></button><button type="button" aria-label={`Scroll ${label.toLowerCase()} right`} disabled={!navigation.right} onClick={() => scroll(1)}><ChevronRight size={17}/></button></div>}
    </div>}
    <div ref={rail} style={rowStyle} className="twe-drinks-advisor__numbered-run-rail" role="list" aria-label={label} tabIndex={navigation.overflow ? 0 : -1} onKeyDown={event => { if (event.key === 'ArrowLeft') {event.preventDefault(); scroll(-1);} else if (event.key === 'ArrowRight') {event.preventDefault(); scroll(1);} }}>{cards}</div>
  </section>;
}

function RecommendationRail({entries, message, bus, prefix, numbered, resultOnly = false}: {entries: RecommendationRailEntry[]; message: TweChatMessage; bus: EventBus; prefix: string; numbered: boolean; resultOnly?: boolean}) {
  const first = entries[0].ordinal;
  const last = entries.at(-1)!.ordinal;
  const label = numbered ? `Recommendations ${first} to ${last}` : `${entries.length} recommendations`;
  // Result-only runs share one horizontal row even when every label differs.
  // Existing paired runs retain their established category splitting.
  const rows=resultOnly ? [{key:'all',entries,hideEntryHeading:false}] : recommendationRows(entries,numbered);
  const contentType = numbered ? 'numbered_recommendation_group' : 'recommendation_group';
  return <section className={`twe-drinks-advisor__content-block twe-drinks-advisor__numbered-run twe-drinks-advisor__recommendation-rail${numbered ? ' twe-drinks-advisor__recommendation-rail--numbered' : ''}`} data-content-type={contentType} data-block-id={entries.map(entry => `${entry.sourceMessageId || entry.messageBlock?.id || ''}:${entry.resultBlock.id}:${entry.productIndex ?? 'all'}`).join('|')} aria-label={label}>
    {rows.map((row,index)=><RecommendationRow key={`${prefix}:row:${row.key}`} row={row} message={message} bus={bus} prefix={prefix} label={row.title?`${row.title} recommendations`:rows.length===1?label:`Recommendations ${index+1}`} legacy={numbered}/>)}
  </section>;
}

export function OrderedAssistant({message, bus, context, isStreaming, onRefinement, onQuestion}: {
  message: TweChatMessage; bus: EventBus; context: EventContext; isStreaming: boolean;
  onRefinement: (text: string) => void; onQuestion: (text: string) => void;
}) {
  const blocks = displayBlocks(message);
  const items = orderedPresentation(message);
  const prefix = `${message.id}:${message.renderAttempt || 0}`;
  return <div className="twe-drinks-advisor__ordered-content">
    {items.map(item => {
      if (item.type === 'singleton_recommendation') return <SingletonRecommendation key={`${prefix}:${item.messageBlock.id}:${item.resultBlock.id}`} item={item} message={message} bus={bus} prefix={prefix}/>;
      if (item.type === 'numbered_recommendation_group') return <RecommendationRail key={`${prefix}:numbered:${item.entries[0].resultBlock.id}`} entries={item.entries} message={message} bus={bus} prefix={prefix} numbered/>;
      if (item.type === 'recommendation_group') return <RecommendationRail key={`${prefix}:recommendations:${item.entries[0].resultBlock.id}`} entries={item.entries} message={message} bus={bus} prefix={prefix} numbered={false} resultOnly={item.resultOnly}/>;
      const block = item.block;
      const index = blocks.indexOf(block);
      const key = `${prefix}:${block.id}`;
      if (block.type === 'message') {
        if (!block.text.trim()) return null;
        const followedByResult = blocks[index + 1]?.type === 'search_result';
        return <div key={key} className={`twe-drinks-advisor__content-block twe-drinks-advisor__prose-bubble${followedByResult ? ' twe-drinks-advisor__prose-bubble--before-result' : ''}`} data-content-type="message" data-block-id={block.id}><AssistantMarkdown text={block.text}/></div>;
      }
      if (block.type === 'search_result') {
        const group = message.groups?.[block.groupIndex];
        return group ? <ProductGroup key={key} group={group} message={message} index={block.groupIndex} bus={bus}/> : null;
      }
      if (block.type === 'follow_up_refinement') return <div key={key} className="twe-drinks-advisor__content-block" data-content-type={block.type} data-block-id={block.id}><FollowUpRefinement question={block.question} options={block.options} bus={bus} context={context} displayKey={`refinement:${key}`} onOptionClick={onRefinement} isDisabled={isStreaming}/></div>;
      return <div key={key} className="twe-drinks-advisor__content-block twe-drinks-advisor__follow-up-questions" data-content-type={block.type} data-block-id={block.id}><SuggestedQuestions questions={block.questions.map(question => ({label: question, value: question}))} onSelect={onQuestion} disabled={isStreaming} ariaLabel={resolveAgentContent().chatWindow.suggestedFollowUpQuestionsAriaLabel}/></div>;
    })}
  </div>;
}
